class SWReviewsBrowser extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.copy = JSON.parse(this.querySelector('[data-review-copy]').textContent);
    this.grid = this.querySelector('[data-review-grid]');
    this.picker = this.querySelector('[data-product-picker]');
    this.more = this.querySelector('[data-load-reviews]');
    this.status = this.querySelector('[data-review-status]');
    this.error = this.querySelector('[data-review-error]');
    this.productLink = this.querySelector('[data-product-link]');
    this.page = 1;
    this.activeSelection = 'all';
    this.total = Number(this.dataset.total);
    this.picker.addEventListener('change', () => this.load(1), { signal: this.controller.signal });
    this.more.addEventListener('click', () => this.load(this.page + 1), { signal: this.controller.signal });
    this.updateStatus();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.request?.abort();
    this.controller = null;
  }

  phrase(key, variables = {}) {
    let text = this.copy[key];
    for (const [name, value] of Object.entries(variables)) text = text.replaceAll(`__${name}__`, value);
    return text;
  }

  node(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  plainText(value) {
    const decoder = document.createElement('textarea');
    decoder.innerHTML = String(value || '').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
    return decoder.value;
  }

  reviewCard(review) {
    const card = this.node('article', 'sw-customer-review');
    card.dataset.reviewId = review.uuid;
    const header = this.node('header');
    const date = new Date(review.created_at);
    const locale = document.documentElement.lang || 'en-GB';
    const time = this.node('time', '', new Intl.DateTimeFormat(locale.startsWith('en') ? 'en-GB' : locale, { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date));
    time.dateTime = date.toISOString().slice(0, 10);
    header.append(this.node('strong', '', this.plainText(review.reviewer_name)), time);
    const rating = Math.max(0, Math.min(5, Number(review.rating)));
    const stars = this.node('div', 'sw-customer-review-stars', '★'.repeat(rating) + '☆'.repeat(5 - rating));
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', this.phrase('rating_label', { rating }));
    card.append(header, stars);
    const body = review.body || '';
    if (body.startsWith('Store note: Etsy star rating only.')) card.append(this.node('p', 'sw-customer-review-note', body));
    else if (body.length > 280) {
      const details = this.node('details', 'sw-customer-review-text');
      const summary = this.node('summary');
      summary.append(this.node('span', 'sw-customer-review-excerpt', body.slice(0, 227) + '…'), this.node('span', 'sw-customer-review-expand', this.copy.read_more));
      details.append(summary, this.node('p', '', body));
      card.append(details);
    } else card.append(this.node('p', 'sw-customer-review-body', body));
    const footer = this.node('footer');
    if (review.product_in_store && review.product_url?.startsWith('/products/')) {
      const link = this.node('a', '', this.plainText(review.product_title));
      link.href = review.product_url + '#judgeme_product_reviews';
      footer.append(link);
    } else footer.append(this.node('span', '', this.plainText(review.product_title || this.copy.store)));
    footer.append(this.node('small', '', review.transparency_badges?.includes('review_collected_from_another_provider') ? this.copy.imported : this.copy.customer_review));
    card.append(footer);
    return card;
  }

  updateStatus() {
    this.status.textContent = this.phrase('showing', { count: this.grid.children.length, total: this.total });
    this.more.hidden = this.grid.children.length >= this.total;
  }

  async load(page) {
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    const option = this.picker.selectedOptions[0];
    const selection = this.picker.value;
    const total = selection === 'all' ? Number(this.dataset.total) : selection === 'store' ? Number(this.dataset.storeCount) : Number(option.dataset.count);
    const params = new URLSearchParams({ shop_domain: this.dataset.shop, platform: 'shopify', page, per_page: 8, show_media_only: false, display_order: 'most_recent', review_selection: selection === 'all' ? 'all' : selection === 'store' ? 'store_reviews' : 'current_product' });
    if (selection !== 'all' && selection !== 'store') params.set('product_id', selection);
    this.setAttribute('aria-busy', 'true');
    this.more.disabled = true;
    this.status.textContent = this.copy.loading;
    this.error.hidden = true;
    try {
      const response = await fetch(`https://api.judge.me/widgets/reviews_grid_widget_data?${params}`, { signal: request.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Review request failed');
      const data = await response.json();
      if (request.signal.aborted || !this.isConnected) return;
      if (!Array.isArray(data.reviews)) throw new Error('Invalid review response');
      if (page === 1) this.grid.replaceChildren();
      const existing = new Set(Array.from(this.grid.children, card => card.dataset.reviewId));
      for (const review of data.reviews) if (!existing.has(review.uuid)) { this.grid.append(this.reviewCard(review)); existing.add(review.uuid); }
      this.page = page;
      this.activeSelection = selection;
      this.total = Number.isFinite(Number(data.total_count)) ? Number(data.total_count) : total;
      this.productLink.href = option.dataset.url || '/pages/browse';
      this.productLink.textContent = option.dataset.url ? this.copy.product_link : this.copy.browse_shop;
      this.updateStatus();
      if (!this.grid.children.length) this.status.textContent = this.copy.empty;
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.error.textContent = this.copy.error;
      this.error.hidden = false;
      this.picker.value = this.activeSelection;
      this.updateStatus();
    } finally {
      if (this.request === request) { this.removeAttribute('aria-busy'); this.more.disabled = false; }
    }
  }
}

if (!customElements.get('sw-reviews-browser')) customElements.define('sw-reviews-browser', SWReviewsBrowser);
